<template>
  <el-dialog
    :visible="visible"
    @close="$emit('cancelled')"
    :title="rule ? $t('loyalty.button.edit_rule') : $t('loyalty.button.add_rule')"
    width="900px"
    append-to-body
    custom-class="rule-form-dialog">
    <div class="px-6 py-4">
      <el-form :model="form" :rules="rules" ref="ruleForm" label-width="200px" class="rule-form">
        <el-form-item :label="$t('loyalty.label.rule_name')" prop="name" class="mb-6">
          <el-input
            v-model="form.name"
            maxlength="255"
            :placeholder="$t('loyalty.placeholder.rule_name')"
            class="w-full">
          </el-input>
        </el-form-item>

        <el-form-item :label="$t('loyalty.label.description')" prop="description" class="mb-6">
          <el-input
            type="textarea"
            v-model="form.description"
            :rows="3"
            :placeholder="$t('loyalty.placeholder.description')"
            class="w-full">
          </el-input>
        </el-form-item>

        <div class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.rule_type')" prop="rule_type">
            <el-select v-model="form.rule_type" style="width: 100%" @change="handleRuleTypeChange">
              <el-option :label="$t('loyalty.rule_type.global')" value="global"></el-option>
              <el-option :label="$t('loyalty.rule_type.category')" value="category"></el-option>
              <el-option :label="$t('loyalty.rule_type.segment')" value="segment"></el-option>
              <el-option :label="$t('loyalty.rule_type.promotion')" value="promotion"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.priority')" prop="priority">
            <el-input-number
              v-model="form.priority"
              :min="0"
              class="w-full">
            </el-input-number>
            <p class="text-xs text-gray-500 mt-1">{{ $t('loyalty.placeholder.priority') }}</p>
          </el-form-item>
        </div>

        <el-form-item
          v-if="form.rule_type === 'category'"
          :label="$t('loyalty.label.category')"
          prop="category_id"
          class="mb-6">
          <el-select
            v-model="form.category_id"
            filterable
            remote
            :remote-method="searchCategories"
            :loading="categoriesLoading"
            placeholder="Search for category"
            style="width: 100%">
            <el-option
              v-for="category in categories"
              :key="category.id"
              :label="category.name"
              :value="category.id">
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="form.rule_type === 'segment'"
          :label="$t('loyalty.label.customer_segment')"
          prop="customer_segment"
          class="mb-6">
          <el-input
            v-model="form.customer_segment"
            maxlength="50"
            :placeholder="$t('loyalty.placeholder.customer_segment')"
            class="w-full">
          </el-input>
        </el-form-item>

        <div class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.points_multiplier')" prop="points_multiplier">
            <el-input-number
              v-model="form.points_multiplier"
              :min="0"
              :precision="2"
              class="w-full">
            </el-input-number>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.min_purchase_amount')" prop="min_purchase_amount">
            <el-input-number
              v-model="form.min_purchase_amount"
              :min="0"
              :precision="2"
              class="w-full">
            </el-input-number>
          </el-form-item>
        </div>

        <el-form-item :label="$t('loyalty.label.max_points_per_transaction')" prop="max_points_per_transaction" class="mb-6">
          <el-input-number
            v-model="form.max_points_per_transaction"
            :min="1"
            class="w-full">
          </el-input-number>
          <p class="text-xs text-gray-500 mt-1">{{ $t('loyalty.placeholder.max_points_per_transaction') }}</p>
        </el-form-item>

        <div class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.valid_from')" prop="valid_from">
            <el-date-picker
              v-model="form.valid_from"
              type="date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              placeholder="Select date"
              style="width: 100%">
            </el-date-picker>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.valid_until')" prop="valid_until">
            <el-date-picker
              v-model="form.valid_until"
              type="date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              placeholder="Select date"
              :picker-options="{ disabledDate: (time) => form.valid_from && time.getTime() < new Date(form.valid_from).getTime() }"
              style="width: 100%">
            </el-date-picker>
          </el-form-item>
        </div>

        <el-form-item :label="$t('loyalty.label.is_active')" prop="is_active" class="mb-6">
          <el-switch v-model="form.is_active"></el-switch>
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer px-6 py-4 bg-gray-50 flex justify-end space-x-3">
      <button
        @click="$emit('cancelled')"
        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors">
        {{ $t('button.cancel') }}
      </button>
      <button
        @click="save"
        :disabled="saving"
        class="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
        <span v-if="saving" class="flex items-center">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ $t('status.loading') }}
        </span>
        <span v-else>{{ $t('button.save') }}</span>
      </button>
    </div>
  </el-dialog>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'RuleFormDialog',
  mixins: [ErrorHandlingMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    rule: {
      type: Object,
      default: null
    },
    programId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      saving: false,
      categories: [],
      categoriesLoading: false,
      form: {
        name: '',
        description: '',
        rule_type: 'global',
        category_id: null,
        customer_segment: '',
        points_multiplier: 1.0,
        min_purchase_amount: null,
        valid_from: null,
        valid_until: null,
        max_points_per_transaction: null,
        is_active: true,
        priority: 0,
        conditions: {}
      }
    }
  },
  computed: {
    rules () {
      return {
        name: [
          { required: true, message: this.$t('loyalty.validation.rule_name_required'), trigger: 'blur' },
          { max: 255, message: 'Name cannot exceed 255 characters', trigger: 'blur' }
        ],
        rule_type: [
          { required: true, message: 'Rule type is required', trigger: 'blur' }
        ],
        points_multiplier: [
          { required: true, message: 'Points multiplier is required', trigger: 'blur' },
          { type: 'number', min: 0, message: 'Must be positive', trigger: 'blur' }
        ],
        category_id: [
          { required: this.form.rule_type === 'category', message: 'Category is required for category rules', trigger: 'change' }
        ],
        customer_segment: [
          { required: this.form.rule_type === 'segment', message: 'Customer segment is required for segment rules', trigger: 'blur' },
          { max: 50, message: 'Customer segment cannot exceed 50 characters', trigger: 'blur' }
        ],
        valid_until: [
          {
            validator: (rule, value, callback) => {
              if (value && this.form.valid_from && new Date(value) < new Date(this.form.valid_from)) {
                callback(new Error('Valid until must be after valid from'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      }
    }
  },
  watch: {
    rule: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.form = {
            name: newVal.name || '',
            description: newVal.description || '',
            rule_type: newVal.rule_type || 'global',
            category_id: newVal.category_id || null,
            customer_segment: newVal.customer_segment || '',
            points_multiplier: parseFloat(newVal.points_multiplier) || 1.0,
            min_purchase_amount: newVal.min_purchase_amount || null,
            valid_from: newVal.valid_from || null,
            valid_until: newVal.valid_until || null,
            max_points_per_transaction: newVal.max_points_per_transaction || null,
            is_active: newVal.is_active !== undefined ? newVal.is_active : true,
            priority: newVal.priority || 0,
            conditions: newVal.conditions || {}
          }
          if (newVal.category_id) {
            this.loadCategory(newVal.category_id)
          }
        } else {
          this.resetForm()
        }
      }
    },
    visible (newVal) {
      if (newVal && this.$refs.ruleForm) {
        this.$refs.ruleForm.clearValidate()
      }
    }
  },
  methods: {
    resetForm () {
      this.form = {
        name: '',
        description: '',
        rule_type: 'global',
        category_id: null,
        customer_segment: '',
        points_multiplier: 1.0,
        min_purchase_amount: null,
        valid_from: null,
        valid_until: null,
        max_points_per_transaction: null,
        is_active: true,
        priority: 0,
        conditions: {}
      }
      this.categories = []
    },
    handleRuleTypeChange () {
      // Reset type-specific fields when type changes
      if (this.form.rule_type !== 'category') {
        this.form.category_id = null
      }
      if (this.form.rule_type !== 'segment') {
        this.form.customer_segment = ''
      }
    },
    async searchCategories (query) {
      if (!query) {
        this.categories = []
        return
      }
      try {
        this.categoriesLoading = true
        const response = await this.$categoryService.search({ q: query, limit: 20 })
        this.categories = response.data || []
      } catch (error) {
        console.error('Error searching categories:', error)
      } finally {
        this.categoriesLoading = false
      }
    },
    async loadCategory (categoryId) {
      try {
        const category = await this.$categoryService.get(categoryId)
        this.categories = [category]
      } catch (error) {
        console.error('Error loading category:', error)
      }
    },
    async save () {
      this.$refs.ruleForm.validate(async (valid) => {
        if (!valid) return

        try {
          this.saving = true
          const ruleData = {
            ...this.form,
            loyalty_program_id: this.programId
          }
          // Remove null values for optional fields
          Object.keys(ruleData).forEach(key => {
            if (ruleData[key] === null || ruleData[key] === '') {
              delete ruleData[key]
            }
          })
          let result
          if (this.rule) {
            result = await this.$store.dispatch('loyalty/rules/updateRule', {
              id: this.rule.id,
              data: ruleData
            })
            this.showSuccess(this.$t('loyalty.message.rule_updated'))
          } else {
            result = await this.$store.dispatch('loyalty/rules/createRule', ruleData)
            this.showSuccess(this.$t('loyalty.message.rule_created'))
          }
          this.$emit('saved', result)
        } catch (error) {
          console.error('Error saving rule:', error)
          this.globalErrHandle(error)
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.rule-form-dialog ::v-deep .el-dialog__body {
  padding: 0;
}

.rule-form ::v-deep .el-form-item__label {
  font-weight: 500;
  color: #374151;
}

.rule-form ::v-deep .el-input__inner,
.rule-form ::v-deep .el-textarea__inner {
  border-radius: 0.5rem;
  border-color: #d1d5db;
}

.rule-form ::v-deep .el-input__inner:focus,
.rule-form ::v-deep .el-textarea__inner:focus {
  border-color: #3b82f6;
}
</style>
